import { useEffect } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';

type Args = {
  /**
   * DOM id of the element to scroll to (used with `document.getElementById`).
   * In most cases this also implies the expected hash via the default `hash = #${elementId}`.
   */
  elementId: string;
  /** Enables/disables the scroll behavior (useful when the target element isn't rendered yet). */
  enabled?: boolean;
  /**
   * URL hash that must match `location.hash` before scrolling happens.
   * Pass this only when the hash you navigate to is different from `#${elementId}` (e.g. legacy/alias hash).
   */
  hash?: string;
  /** When true, scrolls so the element is centered instead of aligned to the top. */
  isCentered?: boolean;
  /** Optional delay (ms) before attempting to scroll, to allow the target element to mount. */
  delay?: number;
};

// Scrolls to the target element when the current URL hash matches.
const useScrollToHashElement = ({
  elementId,
  enabled = true,
  hash = `#${elementId}`,
  isCentered,
  delay = 0,
}: Args): void => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled || location.hash !== hash) return;

    // Wait briefly so the target element is present before scrolling.
    const timeoutId = window.setTimeout(() => {
      document.getElementById(elementId)?.scrollIntoView({
        behavior: 'smooth',
        block: isCentered ? 'center' : 'start',
      });
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [delay, elementId, enabled, hash, isCentered, location.hash]);
};

export default useScrollToHashElement;
